import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";

export function AudioToggle() {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const audio = new Audio("/assets/ocean-ambient.wav");
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0.45;
    audio.muted = true;
    audioRef.current = audio;

    const onReady = () => setReady(true);
    audio.addEventListener("canplaythrough", onReady, { once: true });
    audio.addEventListener("loadeddata", onReady, { once: true });

    // Attempt autoplay muted (best practice, browsers allow this)
    const tryAutoplay = async () => {
      try {
        await audio.play();
      } catch {}
    };
    tryAutoplay();

    return () => {
      audio.pause();
      audio.src = "";
      audio.removeEventListener("canplaythrough", onReady);
      audio.removeEventListener("loadeddata", onReady);
      audioRef.current = null;
    };
  }, []);

  // Persist preference
  useEffect(() => {
    try {
      const saved = localStorage.getItem("poseidon_audio");
      if (saved === "on") {
        setTimeout(() => handleToggle(true), 600);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = useCallback(async (forceOn) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextPlaying = typeof forceOn === "boolean" ? forceOn : !playing;
    try {
      if (nextPlaying) {
        audio.muted = false;
        await audio.play();
      } else {
        audio.muted = true;
      }
      setPlaying(nextPlaying);
      try {
        localStorage.setItem("poseidon_audio", nextPlaying ? "on" : "off");
      } catch {}
    } catch (e) {
      // Fail silently
    }
  }, [playing]);

  return (
    <button
      type="button"
      onClick={() => handleToggle()}
      aria-label={playing ? t("hud.audioOff") : t("hud.audioOn")}
      title={playing ? t("hud.audioOff") : t("hud.audioOn")}
      data-testid="audio-toggle-button"
      className={[
        "glass rounded-full flex items-center gap-2 pl-3 pr-4 h-11 select-none",
        "text-[var(--paper-50)] transition-all duration-300",
        "hover:border-[rgba(240,196,90,0.32)] hover:-translate-y-[1px]",
        playing ? "ring-gold" : "",
        !ready ? "opacity-80" : "",
      ].join(" ")}
    >
      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[rgba(240,196,90,0.12)]">
        {playing ? (
          <Volume2 size={14} color="#F0C45A" />
        ) : (
          <VolumeX size={14} color="#A9B4C7" />
        )}
      </span>
      <span className="flex items-end gap-[2px] h-[16px]" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`wave-bar ${playing ? "playing" : ""}`}
            style={{ height: playing ? 10 : 4 }}
          />
        ))}
      </span>
      <span className="text-[11px] tracking-[0.14em] uppercase text-[var(--paper-100)]/80 font-mono-brand hidden sm:inline">
        {playing ? "Ocean On" : "Ocean Off"}
      </span>
    </button>
  );
}
